'use client'

/**
 * The send-feedback draft (Issue #13645).
 *
 * ONE owner for the whole draft shape — `message`, `screenshots` and `meta` —
 * write-through to two stores:
 *
 *   - **localStorage**, synchronously on every mutation. Instant and offline,
 *     and the only store an unauthenticated submitter gets.
 *   - **`form_drafts`** via `saveFormDraft`, debounced by
 *     {@link DRAFT_DEBOUNCE_MS}. Server-side and keyed on `(userId, formKey)`,
 *     so a draft resumes on a second device after signing in.
 *
 * Restore prefers the database and falls back to localStorage, because the
 * database row is the one that can have been written by another device.
 *
 * ## What this replaced, and why it is one hook and not two
 *
 * Until #13645 this surface ran on a CRDT document (ADR-0061, now
 * superseded): a sync Exchange over SSE with an IndexedDB store, reached
 * through a lazily-mounted provider. `form_drafts` was already the fallback that
 * unauthenticated submitters got, but only for the message — the fallback
 * component stubbed `addScreenshot`, `removeScreenshot` and `setMeta` to
 * no-ops, so collapsing onto it unchanged would have silently dropped
 * screenshot attachment. Meanwhile the message itself was split between
 * `useFormDraft('feedback-message')` (localStorage) and ad-hoc
 * `saveFormDraft`/`getFormDraft` calls made from the modal on a separate
 * timer. Owning all three fields and both stores here is what makes the modal
 * a single rendering path.
 *
 * `formKey` is deliberately unchanged from the pre-existing
 * `'feedback-message'`, so rows already written by the old path — and
 * localStorage entries already written by `useFormDraft` under the same
 * id — are read, not orphaned.
 *
 * What was knowingly given up: live multi-device sync (text appearing on a
 * phone as it is typed on a laptop) and CRDT merge semantics for concurrent
 * edits to the same draft. Cross-device *resume* survives, because
 * `form_drafts` is keyed on the user rather than the device.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  saveFormDraft,
  getFormDraft,
  deleteFormDraft,
} from '@/app/actions/form-draft-actions'
import { reportClientError } from '@/lib/client-error'
import { ERROR_IDS } from '@/lib/errors/constants'
import {
  DRAFT_PREFIX,
  DRAFT_DEBOUNCE_MS,
  DRAFT_EXPIRY_MS,
} from '@/hooks/use-form-draft'
import { registerBeforeRefresh } from '@/lib/safe-refresh'

/**
 * The `form_drafts.formKey` and the localStorage id. Unchanged from the value
 * the pre-#13645 path used, so in-flight drafts in either store survive the
 * migration — see this module's header.
 */
export const FEEDBACK_DRAFT_FORM_KEY = 'feedback-message'

const STORAGE_KEY = `${DRAFT_PREFIX}${FEEDBACK_DRAFT_FORM_KEY}`

export type FeedbackDraftMeta = {
  feedbackType: string
  pageUrl: string
  /**
   * Reserved for telemetry correlation. Carried through restore/persist so a
   * value written by any future caller round-trips, but nothing writes it
   * today — the modal's analytics id is a per-mount UUID that deliberately
   * does not live in the draft.
   */
  sessionId: string
}

export type FeedbackDraftSnapshot = {
  message: string
  screenshots: readonly string[]
  meta: FeedbackDraftMeta
}

const EMPTY_META: FeedbackDraftMeta = {
  feedbackType: '',
  pageUrl: '',
  sessionId: '',
}

const EMPTY_DRAFT: FeedbackDraftSnapshot = {
  message: '',
  screenshots: [],
  meta: EMPTY_META,
}

/** A draft is worth persisting if it has text OR an attached screenshot. */
function hasContent(draft: FeedbackDraftSnapshot): boolean {
  return draft.message.trim().length > 0 || draft.screenshots.length > 0
}

/**
 * Coerce an unknown persisted payload into a draft.
 *
 * Tolerant on purpose: rows written by the pre-#13645 modal hold
 * `{ message }` alone, and localStorage entries written by `useFormDraft`
 * hold the bare message string. Both must read back as a draft rather than as
 * nothing, or the migration loses exactly the in-flight work it exists to
 * keep.
 */
function coerceDraft(raw: unknown): FeedbackDraftSnapshot | null {
  if (typeof raw === 'string') {
    return raw ? { ...EMPTY_DRAFT, message: raw } : null
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null

  const record = raw as Record<string, unknown>
  const message = typeof record.message === 'string' ? record.message : ''
  const screenshots = Array.isArray(record.screenshots)
    ? record.screenshots.filter((ref): ref is string => typeof ref === 'string')
    : []
  const metaRecord =
    typeof record.meta === 'object' &&
    record.meta !== null &&
    !Array.isArray(record.meta)
      ? (record.meta as Record<string, unknown>)
      : {}
  const meta: FeedbackDraftMeta = {
    feedbackType:
      typeof metaRecord.feedbackType === 'string'
        ? metaRecord.feedbackType
        : '',
    pageUrl: typeof metaRecord.pageUrl === 'string' ? metaRecord.pageUrl : '',
    sessionId:
      typeof metaRecord.sessionId === 'string' ? metaRecord.sessionId : '',
  }

  const draft = { message, screenshots, meta }
  return hasContent(draft) ? draft : null
}

/**
 * Read the localStorage draft, honouring the same 24h expiry `useFormDraft`
 * applies to every other draft in the app.
 */
function loadLocalDraft(): FeedbackDraftSnapshot | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw) as { value?: unknown; timestamp?: number }
    if (
      typeof entry.timestamp === 'number' &&
      Date.now() - entry.timestamp > DRAFT_EXPIRY_MS
    ) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (typeof entry.value !== 'string') return null
    // `value` is a string in both shapes: a serialized draft object written
    // here, or a bare message written by the old `useFormDraft` path.
    try {
      return coerceDraft(JSON.parse(entry.value))
    } catch {
      return coerceDraft(entry.value)
    }
  } catch {
    return null
  }
}

function saveLocalDraft(draft: FeedbackDraftSnapshot): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        value: JSON.stringify(draft),
        timestamp: Date.now(),
      }),
    )
  } catch {
    // Quota or a disabled store — the server copy is the durable one, and a
    // failure here must never interrupt typing.
  }
}

function removeLocalDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Same reasoning as saveLocalDraft.
  }
}

type UseFeedbackDraftOptions = {
  /**
   * Whether a session exists. `saveFormDraft` / `getFormDraft` /
   * `deleteFormDraft` are all `withAuth`-composed, so calling them without one
   * resolves `{ success: false }` every time — a normal state for the feedback
   * modal, which anonymous visitors can open. Passing `false` keeps the draft
   * localStorage-only instead of generating a guaranteed-failing round trip
   * per debounce tick.
   */
  isAuthenticated: boolean
  /**
   * Whether the modal is open. Restore runs on each open so a draft written on
   * another device shows up on the next one, rather than only at first mount.
   */
  isOpen: boolean
}

export type UseFeedbackDraftResult = {
  message: string
  screenshots: readonly string[]
  meta: FeedbackDraftMeta
  setMessage: (value: string) => void
  addScreenshot: (ref: string) => void
  removeScreenshot: (index: number) => void
  setMeta: (updates: Partial<Omit<FeedbackDraftMeta, 'sessionId'>>) => void
  /** Drop the draft from state and from both stores. */
  clearDraft: () => void
  /** Persist now, bypassing the debounce (pre-submit, or on close). */
  flushDraft: () => void
  /**
   * Issue #14531 — true when a SAVED draft with content was restored into
   * this form (the localStorage hydration, or the open-time `form_drafts`
   * restore), and nothing has discarded it since. Gates the modal's "Start
   * over" control. Stays false when the submitter typed before the restore
   * landed, because that restore is skipped; `clearDraft` resets it.
   */
  wasRestored: boolean
  /**
   * Put a snapshot back — the Undo half of "Start over". Written through like
   * any edit (localStorage now, `form_drafts` on the debounce), so an undone
   * reset survives a reload rather than living only in state.
   */
  restoreDraft: (snapshot: FeedbackDraftSnapshot) => void
}

export function useFeedbackDraft({
  isAuthenticated,
  isOpen,
}: UseFeedbackDraftOptions): UseFeedbackDraftResult {
  const [draft, setDraft] = useState<FeedbackDraftSnapshot>(
    () => loadLocalDraft() ?? EMPTY_DRAFT,
  )
  // The `hasContent` check is what makes this a restore signal: the initial
  // draft came from localStorage (or is `EMPTY_DRAFT`), and only one with
  // content is a saved draft worth calling "restored".
  const [wasRestored, setWasRestored] = useState(() => hasContent(draft))

  // Mirrors of state for the callbacks that have to read the CURRENT draft
  // without re-subscribing (the debounce timer, the refresh handler, flush).
  const draftRef = useRef(draft)
  draftRef.current = draft
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined)
  /**
   * True while the draft's CONTENT came only from a store (the localStorage
   * hydration above, or a server restore) and no deliberate edit has touched
   * it since. The server restore below is gated on this rather than on "is the
   * draft empty": a draft hydrated from localStorage is NOT a reason to skip
   * the database, whose row is the one another device could have written, but
   * a character the submitter has typed in THIS tab is.
   *
   * `setMeta` deliberately does NOT clear it. Picking a feedback type is not
   * draft content, and a caller that opens the modal with an `initialType`
   * would otherwise suppress the restore of a message the submitter had
   * already written somewhere else.
   */
  const pristineRef = useRef(true)

  /**
   * Whether the submitter picked a feedback type IN THIS SESSION.
   *
   * Distinct from `meta.feedbackType` being non-empty, which is also true of a
   * type that merely came back from localStorage hydration or from a previous
   * restore. Only a deliberate in-session choice outranks the stored row when
   * the async restore lands — hydration must still lose to the database, which
   * is the whole point of restoring from it.
   */
  const typeChosenRef = useRef(false)

  const cancelPendingSave = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = undefined
    }
  }, [])

  /**
   * Push the current draft to the server.
   *
   * A resolved `{ success: false }` is REPORTED, not swallowed. An earlier
   * revision justified the silence with "with no session that is the expected
   * answer" — but every call site guards on `isAuthenticated` first, so that
   * case never reaches here. On the paths that do, `{ success: false }` means
   * `Draft data too large`, `Invalid form key`, or the action's error boundary
   * fired, and nothing on screen would say the draft had stopped reaching
   * the server while they kept typing.
   */
  const persistToServer = useCallback(
    (snapshot: FeedbackDraftSnapshot, action: string) => {
      saveFormDraft(FEEDBACK_DRAFT_FORM_KEY, {
        message: snapshot.message,
        screenshots: [...snapshot.screenshots],
        meta: snapshot.meta,
      })
        .then(result => {
          if (result.success) return
          // `result.error` is the action's catch-all string, not a signal to
          // branch on — it is surfaced for whoever reads the report, and
          // nothing here reads it back.
          reportClientError({
            errorId: ERROR_IDS.FEEDBACK_MODAL_DRAFT_SAVE_FAILED,
            action,
            error: new Error(
              result.error || 'saveFormDraft returned success: false',
            ),
          })
        })
        .catch(error => {
          // A REJECTED promise is a genuine failure (network, serialization),
          // unlike a resolved `{ success: false }` — worth reporting.
          reportClientError({
            errorId: ERROR_IDS.FEEDBACK_MODAL_DRAFT_SAVE_FAILED,
            action,
            error,
          })
        })
    },
    [],
  )

  /**
   * Every mutation goes through here: state, the instant local write, and the
   * armed debounce, in one place so no caller can persist half of a change.
   */
  const mutate = useCallback(
    (
      update: (current: FeedbackDraftSnapshot) => FeedbackDraftSnapshot,
      { isContentEdit = true }: { isContentEdit?: boolean } = {},
    ) => {
      const next = update(draftRef.current)
      if (next === draftRef.current) return
      if (isContentEdit) pristineRef.current = false
      draftRef.current = next
      setDraft(next)

      if (hasContent(next)) {
        saveLocalDraft(next)
      } else {
        removeLocalDraft()
      }

      cancelPendingSave()

      if (!hasContent(next)) {
        // The draft just went empty. Clearing local state and stopping is not
        // enough: the `form_drafts` row still holds the old message, and the
        // next open restores it — so a select-all + Delete silently reverts.
        // Only an explicit Discard reached `deleteFormDraft` before this.
        //
        // Gated on `isContentEdit` so this fires on a real edit to empty and
        // not on a restore or a `setMeta`; and on `pristineRef` being false,
        // so an empty draft that was never typed in does not issue a delete
        // for a row it has not read yet.
        if (isContentEdit && !pristineRef.current && isAuthenticated) {
          debounceRef.current = setTimeout(() => {
            debounceRef.current = undefined
            void deleteFormDraft(FEEDBACK_DRAFT_FORM_KEY).catch(error => {
              reportClientError({
                errorId: ERROR_IDS.FEEDBACK_MODAL_DRAFT_SAVE_FAILED,
                action: 'feedback_draft.autodelete',
                error,
              })
            })
          }, DRAFT_DEBOUNCE_MS)
        }
        return
      }

      debounceRef.current = setTimeout(() => {
        debounceRef.current = undefined
        // Anonymous: localStorage already holds it, and that IS the save.
        if (pristineRef.current || !isAuthenticated) return
        persistToServer(draftRef.current, 'feedback_draft.autosave')
      }, DRAFT_DEBOUNCE_MS)
    },
    [cancelPendingSave, isAuthenticated, persistToServer],
  )

  const setMessage = useCallback(
    (value: string) => {
      mutate(current =>
        current.message === value ? current : { ...current, message: value },
      )
    },
    [mutate],
  )

  const addScreenshot = useCallback(
    (ref: string) => {
      mutate(current => ({
        ...current,
        screenshots: [...current.screenshots, ref],
      }))
    },
    [mutate],
  )

  const removeScreenshot = useCallback(
    (index: number) => {
      mutate(current => {
        if (index < 0 || index >= current.screenshots.length) return current
        return {
          ...current,
          screenshots: current.screenshots.filter((_, i) => i !== index),
        }
      })
    },
    [mutate],
  )

  const setMeta = useCallback(
    (updates: Partial<Omit<FeedbackDraftMeta, 'sessionId'>>) => {
      // A non-empty `feedbackType` arriving through this callback is the
      // submitter clicking one of the four cards. Recorded so a restore that
      // resolves afterwards merges around it rather than replacing it.
      if (updates.feedbackType) typeChosenRef.current = true
      mutate(
        current => ({
          ...current,
          meta: { ...current.meta, ...updates },
        }),
        { isContentEdit: false },
      )
    },
    [mutate],
  )

  const clearDraft = useCallback(() => {
    cancelPendingSave()
    // Discarding is itself a deliberate action: a restore must not put the
    // discarded draft straight back.
    pristineRef.current = false
    // The discarded draft's type is discarded with it. Leaving this set would
    // make a later restore merge a type the submitter has just thrown away
    // around a row they have not seen.
    typeChosenRef.current = false
    draftRef.current = EMPTY_DRAFT
    setDraft(EMPTY_DRAFT)
    setWasRestored(false)
    removeLocalDraft()
    if (isAuthenticated) {
      void deleteFormDraft(FEEDBACK_DRAFT_FORM_KEY).catch(() => {
        // Best-effort: the row is overwritten by the next save anyway, and a
        // failure here must not block a successful submit.
      })
    }
  }, [cancelPendingSave, isAuthenticated])

  const restoreDraft = useCallback(
    (snapshot: FeedbackDraftSnapshot) => {
      if (snapshot.meta.feedbackType) typeChosenRef.current = true
      mutate(() => snapshot)
      setWasRestored(hasContent(snapshot))
    },
    [mutate],
  )

  const flushDraft = useCallback(() => {
    cancelPendingSave()
    const snapshot = draftRef.current
    if (!hasContent(snapshot)) return
    saveLocalDraft(snapshot)
    if (isAuthenticated) persistToServer(snapshot, 'feedback_draft.flush')
  }, [cancelPendingSave, isAuthenticated, persistToServer])

  // Restore from the database, preferring it over whatever localStorage holds
  // — that row is the one another device could have written. Runs on each
  // open so a draft started elsewhere appears without a reload, and never
  // overwrites content already present in this tab.
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return
    let cancelled = false

    getFormDraft(FEEDBACK_DRAFT_FORM_KEY)
      .then(result => {
        if (cancelled) return
        if (!result.success || !result.draft) return
        const restored = coerceDraft(result.draft.draftData)
        if (!restored) return
        if (!pristineRef.current) return

        // MERGE, don't replace. `setMeta` deliberately does not clear
        // `pristineRef` — picking a feedback type is not draft content — so
        // this restore still fires after the submitter has chosen one.
        // Replacing the whole snapshot then silently swapped their selection
        // for the stored draft's (often `''`) if they clicked Bug before
        // `getFormDraft` resolved.
        //
        // Gated on `typeChosenRef`, NOT on the current type being non-empty: a
        // type hydrated from localStorage is also non-empty and must still lose
        // to the database row, which is the reason this restore exists.
        const chosenType = draftRef.current.meta.feedbackType
        const merged: FeedbackDraftSnapshot =
          typeChosenRef.current && chosenType
            ? {
                ...restored,
                meta: { ...restored.meta, feedbackType: chosenType },
              }
            : restored

        draftRef.current = merged
        setDraft(merged)
        setWasRestored(true)
        saveLocalDraft(merged)
      })
      .catch(error => {
        reportClientError({
          errorId: ERROR_IDS.FEEDBACK_MODAL_DRAFT_LOAD_FAILED,
          action: 'feedback_draft.restore',
          error,
        })
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, isAuthenticated])

  // Flush to localStorage before a deliberate page reload, and drop the timer
  // on unmount so a closed modal cannot fire a save after the fact.
  useEffect(() => {
    const unregister = registerBeforeRefresh(() => {
      if (hasContent(draftRef.current)) saveLocalDraft(draftRef.current)
    })
    return () => {
      cancelPendingSave()
      unregister()
    }
  }, [cancelPendingSave])

  return {
    message: draft.message,
    screenshots: draft.screenshots,
    meta: draft.meta,
    setMessage,
    addScreenshot,
    removeScreenshot,
    setMeta,
    clearDraft,
    flushDraft,
    wasRestored,
    restoreDraft,
  }
}
